import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { Card } from '../ui/Card';
import { Alert } from '../ui/Alert';
import { useToast, ColorPickerModal } from '../ui';
import PageHeader from '../ui/PageHeader';
import { PhotoIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { settingsAPI } from '../../services/api';
import useTelemetry from '../../hooks/useTelemetry';
import { prepareLogo } from '../../utils/prepareLogo';
import { LOGO_ACCEPT, LOGO_MAX_BYTES, LOGO_HINT, LOGO_TOO_LARGE } from '../../utils/logoRules';

/**
 * BrandingPage — Settings › Branding.
 *
 * Iter18 Polish Round 2 §2.3 · SET-2 wire parity rebuild.
 *
 * Layout: single vertical stack of cards
 *   1. Logo (thumbnail + Replace/Remove · Logo position segmented L/C/R)
 *   2. Brand Colors (3 rows: Header · Secondary · Text — each opens ColorPickerModal)
 *   3. Preview (doc-header preview)
 *   4. Email Signature (WYSIWYG + include-logo toggle + size select)
 *   5. Save Changes (page-level footer)
 */


const BRAND_COLOR_ROWS = [
  { key: 'primary_color',   label: 'Header color',    fallback: '#D70321' },
  { key: 'secondary_color', label: 'Secondary color', fallback: '#4A4A4A' },
  { key: 'text_color',      label: 'Text color',      fallback: '#1F1F1F' },
];

const LOGO_POSITIONS = [
  { value: 'left',   label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right',  label: 'Right' },
];

const SIGNATURE_LOGO_SIZES = [
  { value: 'small',    label: 'Small' },
  { value: 'best_fit', label: 'Best fit' },
  { value: 'original', label: 'Original size' },
];

// Iter18 R2 Round 3 (B): heights used by the live email-signature preview
// so the operator can see how their choice will render before sending.
const SIGNATURE_LOGO_HEIGHTS = {
  small: 24,
  best_fit: 40,
  original: 56,
};

function formatBytes(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function BrandingPage() {
  const [branding, setBranding] = useState({
    logo_url: '',
    logo_meta: null, // { filename, size, type }
    primary_color: '#D70321',
    secondary_color: '#4A4A4A',
    text_color: '#1F1F1F',
    logo_position: 'left',
    email_signature: '',
    include_logo_in_signature: false,
    signature_logo_size: 'best_fit',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [pickerRow, setPickerRow] = useState(null); // {key, label, fallback} or null
  const { emit } = useTelemetry();
  const toast = useToast();
  const fileInputRef = useRef(null);

  const fetchBranding = useCallback(async () => {
    try {
      setLoading(true);
      const res = await settingsAPI.getBranding();
      if (res) {
        setBranding((prev) => ({ ...prev, ...res }));
      }
    } catch (err) {
      setError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBranding(); }, [fetchBranding]);

  const handleChange = (field, value) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
  };

  // Iter18 R2 §2.3: fixed upload button — dedicated ref, explicit click handler
  // (avoids the label-wrapping bug where the button label swallows the click).
  const triggerUpload = () => fileInputRef.current?.click();

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (file.size > LOGO_MAX_BYTES) {
      setError(LOGO_TOO_LARGE);
      return;
    }
    // IDI-649 D1/D3: store a logo small enough for the settings record;
    // on failure the previous logo stays.
    const result = await prepareLogo(file);
    if (!result.ok) {
      setError(result.message);
      return;
    }
    setBranding((prev) => ({ ...prev, logo_url: result.dataUrl, logo_meta: result.meta }));
    setError(null);
    toast.success('Logo uploaded');
  };

  const removeLogo = () => {
    handleChange('logo_url', '');
    handleChange('logo_meta', null);
    toast.success('Logo removed');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      await settingsAPI.updateBranding(branding);
      toast.success('Branding saved');
      emit('settings.branding_updated', { has_logo: !!branding.logo_url });
    } catch (err) {
      setError('Failed to save branding settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="space-y-3 w-full">
          <div className="h-8 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
          <div className="h-32 rounded animate-pulse" style={{ backgroundColor: 'var(--neutral-100)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 space-y-6">
      <PageHeader
        title="Branding"
        subtitle="Customize how your quotes and invoices look to customers."
      />

      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* 1. Company Logo */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>Company Logo</h3>
          <p className="text-xs font-normal mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            {`Appears on quotes, invoices, and (optionally) email signatures. ${LOGO_HINT}`}
          </p>
        </div>
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            {branding.logo_url ? (
              <div
                className="flex-shrink-0 h-16 w-32 flex items-center justify-center overflow-hidden"
                style={{
                  backgroundColor: 'var(--color-surface-sunken)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--color-border)',
                }}
              >
                <img
                  src={branding.logo_url}
                  alt="Company logo"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ) : (
              <div
                className="flex-shrink-0 h-16 w-32 flex items-center justify-center"
                style={{
                  backgroundColor: 'var(--color-surface-sunken)',
                  borderRadius: 'var(--radius-md)',
                  border: '1px dashed var(--color-border)',
                }}
              >
                <PhotoIcon className="h-6 w-6" style={{ color: 'var(--color-text-muted)' }} />
              </div>
            )}
            <div className="flex-1 min-w-0">
              {branding.logo_meta ? (
                <>
                  <p className="text-sm font-medium truncate" style={{ color: 'var(--color-text-default)' }}>
                    {branding.logo_meta.filename}
                  </p>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    {formatBytes(branding.logo_meta.size)}
                  </p>
                </>
              ) : (
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  No logo uploaded.
                </p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Button variant="secondary" size="sm" onClick={triggerUpload}>
                  <ArrowUpTrayIcon className="h-4 w-4 mr-1.5" />
                  {branding.logo_url ? 'Replace' : 'Upload'}
                </Button>
                {branding.logo_url && (
                  <Button variant="secondary" size="sm" onClick={removeLogo}>
                    Remove
                  </Button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={LOGO_ACCEPT}
                  onChange={handleLogoUpload}
                  className="hidden"
                  aria-label="Upload company logo"
                />
              </div>
            </div>
          </div>

          {/* Logo position */}
          <div className="mt-6">
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-default)' }}>Logo position</p>
            <div
              className="inline-flex overflow-hidden"
              style={{
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-border)',
              }}
            >
              {LOGO_POSITIONS.map((pos) => {
                const selected = branding.logo_position === pos.value;
                return (
                  <button
                    key={pos.value}
                    type="button"
                    onClick={() => handleChange('logo_position', pos.value)}
                    className="px-4 py-2 text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: selected ? 'var(--color-chrome)' : 'transparent',
                      color: selected ? '#fff' : 'var(--color-text-muted)',
                    }}
                  >
                    {pos.label}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>
              Position of your logo on document headers.
            </p>
          </div>
        </div>
      </Card>

      {/* 2. Brand Colors */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>Brand Colors</h3>
          <p className="text-xs font-normal mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            Applied to document headers, accents, and body text on quotes and invoices.
          </p>
        </div>
        <div className="divide-y divide-gray-100">
          {BRAND_COLOR_ROWS.map((row) => {
            const current = branding[row.key] || row.fallback;
            return (
              <div key={row.key} className="px-6 py-3 flex items-center gap-3">
                <span
                  className="flex-shrink-0"
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: 'var(--radius-sm)',
                    backgroundColor: current,
                    border: '1px solid var(--color-border)',
                  }}
                  aria-hidden="true"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium" style={{ color: 'var(--color-text-default)' }}>{row.label}</p>
                  <p className="text-xs font-mono" style={{ color: 'var(--color-text-muted)' }}>{current.toUpperCase()}</p>
                </div>
                <Button variant="secondary" size="sm" onClick={() => setPickerRow(row)}>Change</Button>
              </div>
            );
          })}
        </div>
      </Card>

      {/* 3. Preview */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>Preview</h3>
          <p className="text-xs font-normal mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            This is how your document header will render on outgoing quotes and invoices.
          </p>
        </div>
        <div className="p-6" style={{ backgroundColor: 'var(--color-surface-sunken)' }}>
          <div
            className="p-6"
            style={{
              backgroundColor: 'var(--color-surface, #fff)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
            }}
          >
            <div className="flex items-start justify-between mb-6">
              <div
                className={
                  branding.logo_position === 'right'
                    ? 'order-2 text-right'
                    : branding.logo_position === 'center'
                    ? 'w-full text-center'
                    : ''
                }
              >
                {branding.logo_url ? (
                  <img
                    src={branding.logo_url}
                    alt="Logo preview"
                    className="max-h-12 max-w-[150px] object-contain mb-2 inline-block"
                  />
                ) : (
                  <div
                    className="h-10 w-[120px] mb-2 flex items-center justify-center inline-flex"
                    style={{ backgroundColor: 'var(--neutral-100)', borderRadius: 'var(--radius-sm)' }}
                  >
                    <span className="text-[10px]" style={{ color: 'var(--color-text-muted)' }}>LOGO</span>
                  </div>
                )}
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Your Company Name</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>123 Main St, City, ST 12345</p>
              </div>
              <div className={branding.logo_position === 'center' ? 'hidden' : 'text-right'}>
                <h4 className="text-lg font-bold" style={{ color: branding.primary_color }}>QUOTE</h4>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>#Q-000218</p>
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Jan 15, 2026</p>
              </div>
            </div>
            <div
              className="px-3 py-2 text-xs font-medium text-white flex justify-between"
              style={{
                backgroundColor: branding.primary_color,
                borderTopLeftRadius: 'var(--radius-sm)',
                borderTopRightRadius: 'var(--radius-sm)',
              }}
            >
              <span>Item</span><span>Amount</span>
            </div>
            <div style={{ border: '1px solid var(--color-border)', borderTop: 'none' }}>
              <div className="px-3 py-2 text-xs flex justify-between" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ color: branding.text_color }}>Spray Foam - Attic</span>
                <span style={{ color: branding.text_color }}>$2,450.00</span>
              </div>
              <div className="px-3 py-2 text-xs flex justify-between">
                <span style={{ color: branding.text_color }}>Fiberglass - Walls</span>
                <span style={{ color: branding.text_color }}>$1,800.00</span>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <div
                className="px-3 py-1.5 text-xs font-bold text-white"
                style={{ backgroundColor: branding.secondary_color, borderRadius: 'var(--radius-sm)' }}
              >
                Total: $4,250.00
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* 4. Email Signature */}
      <Card>
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="text-sm font-semibold" style={{ color: 'var(--color-text-default)' }}>Email Signature</h3>
          <p className="text-xs font-normal mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            Appended to outgoing emails sent from BIDIT.
          </p>
        </div>
        <div className="px-6 py-4 space-y-4">
          <Textarea
            value={branding.email_signature}
            onChange={(e) => handleChange('email_signature', e.target.value)}
            rows={4}
            className="w-full"
            placeholder="e.g. Best regards, — Jane Doe · Owner · ACME Insulation · (555) 123-4567"
            aria-label="Email signature"
          />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={branding.include_logo_in_signature}
              onChange={(e) => handleChange('include_logo_in_signature', e.target.checked)}
              style={{ accentColor: 'var(--color-action-primary)' }}
            />
            <span className="text-sm" style={{ color: 'var(--color-text-default)' }}>Include company logo in email signature</span>
          </label>

          {branding.include_logo_in_signature && (
            <div className="ml-6">
              <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-muted)' }}>Logo size</p>
              <div className="flex gap-2">
                {SIGNATURE_LOGO_SIZES.map((sz) => {
                  const selected = (branding.signature_logo_size || 'best_fit') === sz.value;
                  return (
                    <button
                      key={sz.value}
                      type="button"
                      onClick={() => handleChange('signature_logo_size', sz.value)}
                      className="px-3 py-1.5 text-xs font-medium transition-colors"
                      style={{
                        backgroundColor: selected ? 'var(--color-status-neutral-bg, var(--neutral-100))' : 'transparent',
                        color: selected ? 'var(--color-text-default)' : 'var(--color-text-muted)',
                        border: `1px solid ${selected ? 'var(--color-border-strong)' : 'var(--color-border)'}`,
                        borderRadius: 'var(--radius-md)',
                      }}
                    >
                      {sz.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Iter18 R2 Round 3 (B): Live email-signature preview. Mirrors the
              onboarding-wizard signature UX so operators see the rendered
              output as they type — including the logo when the toggle is on. */}
          <div className="mt-4">
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--color-text-default)' }}>
              Preview
            </label>
            <div
              className="rounded-md border p-4"
              style={{
                borderColor: 'var(--color-border)',
                backgroundColor: 'var(--color-surface-sunken)',
                minHeight: '5rem',
              }}
            >
              {branding.include_logo_in_signature && branding.logo_url && (
                <img
                  src={branding.logo_url}
                  alt="Company logo"
                  className="mb-2 object-contain"
                  style={{
                    maxHeight: `${SIGNATURE_LOGO_HEIGHTS[branding.signature_logo_size || 'best_fit']}px`,
                    maxWidth: '240px',
                  }}
                />
              )}
              {branding.email_signature ? (
                <pre
                  className="whitespace-pre-wrap text-sm font-sans m-0"
                  style={{ color: 'var(--color-text-default)' }}
                >{branding.email_signature}</pre>
              ) : (
                <p className="text-xs italic" style={{ color: 'var(--color-text-muted)' }}>
                  Your signature preview will appear here as you type.
                </p>
              )}
            </div>
            <p className="text-xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
              This signature is attached to quotes and invoices sent via email.
            </p>
          </div>
        </div>
      </Card>

      {/* 5. Save */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {/* Canonical color picker */}
      <ColorPickerModal
        open={!!pickerRow}
        onClose={() => setPickerRow(null)}
        value={pickerRow ? (branding[pickerRow.key] || pickerRow.fallback) : '#D70321'}
        title={pickerRow ? pickerRow.label : 'Pick a color'}
        onApply={(hex) => {
          if (pickerRow) handleChange(pickerRow.key, hex);
        }}
      />
    </div>
  );
}
